import { useCallback } from 'react';
import { refundStatusColorMap } from '@/constants/subsidy/status';
import type { ResourceApproval } from '@/data/subsidy/interfaces';
import { RefundStatus, StepStatus } from '@/models/subsidy/status';

export enum Step {
  ResourceDescription = 1,
  ResourceApproval = 2,
  InvoiceAttachment = 3,
  InvoiceValidation = 4,
  Payment = 5,
}

export interface StepState {
  disabled: boolean;
  status: StepStatus | null;
}

const statusToStepMap: Record<RefundStatus, Step> = {
  [RefundStatus.ResourceApprovalCanceled]: Step.ResourceApproval,
  [RefundStatus.ResourceApprovalRevisionRequired]: Step.ResourceApproval,
  [RefundStatus.ResourceApprovalPendingApproval]: Step.ResourceApproval,
  [RefundStatus.ResourceApprovalRejected]: Step.ResourceApproval,
  [RefundStatus.ResourceApprovalApproved]: Step.InvoiceAttachment,
  [RefundStatus.ResourceApprovalNotApproved]: Step.ResourceApproval,
  [RefundStatus.RefundRequestCanceled]: Step.InvoiceValidation,
  [RefundStatus.RefundRequestPendingApproval]: Step.InvoiceValidation,
  [RefundStatus.RefundRequestRevisionRequired]: Step.InvoiceValidation,
  [RefundStatus.RefundRequestRejected]: Step.InvoiceValidation,
  [RefundStatus.RefundRequestApproved]: Step.Payment,
  [RefundStatus.RefundRequestPaid]: Step.Payment,
};

export const useSteps = (resourceApproval: ResourceApproval | null) => {
  const status = resourceApproval?.status;
  const currentStep = status
    ? statusToStepMap[status]
    : Step.ResourceDescription;

  const stepStatus = status ? refundStatusColorMap[status] : null;

  const getStatus = useCallback(
    (step: Step): StepStatus | null => {
      const isCurrentStep = step === currentStep;
      const isPastStep = step < currentStep;

      const isRefundRequestWithRevisionRequired =
        step === Step.InvoiceAttachment &&
        status === RefundStatus.RefundRequestRevisionRequired;

      if (isRefundRequestWithRevisionRequired) return StepStatus.Warning;
      if (isPastStep) return StepStatus.Success;
      if (isCurrentStep) return stepStatus;
      return null;
    },
    [currentStep, status, stepStatus]
  );

  const getStepState = (step: Step): StepState => {
    if (!resourceApproval) {
      return {
        disabled: step !== Step.ResourceDescription,
        status: null,
      };
    }

    const isFutureStep = step > currentStep;

    const enableInvoiceAttachment =
      step === Step.InvoiceAttachment &&
      status === RefundStatus.ResourceApprovalPendingApproval;

    return {
      disabled: isFutureStep && !enableInvoiceAttachment,
      status: getStatus(step),
    };
  };

  return { currentStep, stepStatus, getStepState };
};
